import { enumColumn, floatColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getAcquisitionMethodLabels } from "src/api/generated/erp/db/types/enums/acquisitionMethod.ts";
import { TestConfigurationResultViewCommonProps } from "src/components/views/erp/configurator/configuratorTesting/TestConfigurationResultViewCommonProps.ts";
import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import React from "react";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { ResolvedPartConfigurationBomLine } from "src/api/generated/erp/configurator/configurationResolving/bom/resolvedPartConfigurationBomLine";
import { concatWithPipe } from "src/utils/strings.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { createBomLineSubConfiguratorType } from "src/components/views/erp/configurator/configuratorTesting/createBomLineSubConfiguratorType.ts";

export interface TestConfigurationBomDataGridBaseProps extends TestConfigurationResultViewCommonProps {
	fetchRows: () => Promise<ResolvedPartConfigurationBomLine[]>;
	subConfigurationResultsView: (props: TestConfigurationResultViewCommonProps) => React.ReactNode;
	showPricesAndCosts?: boolean;
}

export const TestConfigurationBomDataGridBase = ({
	fetchRows,
	configurationSessionId,
	configuratorType,
	propertyValues,
	subConfigurationResultsView,
	showPricesAndCosts = false,
}: TestConfigurationBomDataGridBaseProps) => {
	const { openDialog } = useGenericDialog();

	return (
		<ClientSideDataGridModel
			fetchData={async () => {
				const rows = await fetchRows();
				return rows.map((row, index) => ({ ...row, index }));
			}}
			getRows={(data) => data}
			getRowId={(row) => row.index}
			initialParams={{}}
			gridId={"AE593DFB73807653"}
			render={({ dataGridProps, onlySelectedRow }) => (
				<AavoDataGrid<ResolvedPartConfigurationBomLineWithIndex>
					columns={[
						integerColumn({
							field: "referenceNo",
							headerName: i18n.t("reference_no"),
							width: 100,
						}),
						textColumn({
							field: "description" as any,
							headerName: i18n.t("part"),
							width: 200,
							valueGetter: (_, row) => getRowDescription(row),
						}),
						floatColumn({
							field: "quantity",
							headerName: i18n.t("quantity"),
							width: 100,
						}),
						floatColumn({
							field: "materialCost",
							headerName: i18n.t("material_cost"),
							width: 120,
						}),
						showPricesAndCosts && [
							floatColumn({
								field: "workCost",
								headerName: i18n.t("labour_cost"),
								width: 120,
							}),
							floatColumn({
								field: "unitCost" as any,
								headerName: i18n.t("unit_cost"),
								width: 120,
								valueGetter: (_, row) => row.materialCost + row.workCost,
							}),
							floatColumn({
								field: "totalCost" as any,
								headerName: i18n.t("total_cost"),
								width: 120,
								valueGetter: (_, row) => (row.materialCost + row.workCost) * row.quantity,
							}),
							floatColumn({
								field: "workingTime",
								headerName: i18n.t("working_time"),
								width: 120,
							}),
							floatColumn({
								field: "unitPriceInSellingCurrency",
								headerName: i18n.t("unit_price"),
							}),
							floatColumn({
								field: "totalPriceInSellingCurrency",
								headerName: i18n.t("total_price"),
							}),
						],
						enumColumn({
							field: "acquisitionMethod",
							headerName: i18n.t("acquisition_method"),
							width: 120,
							enumLabels: getAcquisitionMethodLabels(),
						}),
						textColumn({
							field: "note",
							headerName: i18n.t("note"),
							width: 200,
						}),
					]}
					actionBarComponents={
						<>
							<AsyncButton
								key={"openSubConfiguration"}
								label={i18n.t("sub_configuration")}
								icon={faExternalLink}
								disabled={onlySelectedRow?.bomLineConfiguration == null}
								variant={"outlined"}
								onClick={() => {
									openSubConfigurationResults(onlySelectedRow!);
								}}
							/>
						</>
					}
					onRowDoubleClick={({ row }) => openSubConfigurationResults(row)}
					agentContext={{
						key: defineAgentUiContextKey("erp.configurator.testConfigurationBom.grid"),
						data: { configurationSessionId },
						rowFields: ["index", "bomLineCatalogPartId", "bomLinePartNo", "referenceNo", "quantity"],
						selectedRowPathSegment: (row) => `${i18n.t("row")} ${row.referenceNo ?? row.index}`,
						selectedRowsPathSegment: (count) => i18n.t("rows_selected_count", { count }),
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);

	function openSubConfigurationResults(row: ResolvedPartConfigurationBomLine) {
		const subConfig = row.bomLineConfiguration;
		if (subConfig == null) return;
		openDialog(() => ({
			title: getRowDescription(row),
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.subConfigurationResults.dialog"),
				data: {
					configurationSessionId,
					bomLineCatalogPartId: row.bomLineCatalogPartId ?? null,
					referenceNo: row.referenceNo ?? null,
				},
				pathSegments: [getRowDescription(row)],
			},
			content: subConfigurationResultsView({
				configurationSessionId: configurationSessionId,
				propertyValues: subConfig.propertyValues,
				configuratorType: createBomLineSubConfiguratorType(row, configuratorType, propertyValues),
			}),
		}));
	}
};

function getRowDescription(row: ResolvedPartConfigurationBomLine) {
	return (
		row.bomLineConfiguration?.topLevelScriptResult?.description ??
		concatWithPipe(row.bomLinePartNo, row.bomLinePartDescription1)
	);
}

interface ResolvedPartConfigurationBomLineWithIndex extends ResolvedPartConfigurationBomLine {
	index: number;
}
